import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xxtract/ui';
import { marketName } from '../review.utils';
import type { RetailerChoiceEntry } from '../intake.api';

/** Boven dit aantal wordt de lijst afgekapt; daaronder past hij nog in één oogopslag. */
const MAX_NAMEN = 6;
const GETOOND_BIJ_AFKAPPEN = 5;

export interface ConfirmSubmitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnCount: number;
  changedCount: number;
  /** h5-8 FR38a (EXPERIENCE.md KF-1 citaat): "40 producten, 2 wijzigingen, order wordt aangemaakt". */
  totalProductCount: number;
  productChangeCount: number;
  /**
   * h13-5: naar welke afnemers de producten gaan, per doelmarkt — bij naam, niet alleen het
   * aantal. Dit is het laatste moment waarop een verkeerde afnemer nog opvalt.
   */
  retailerChoice?: RetailerChoiceEntry[];
  /**
   * De doelmarkten van deze aanmelding. Zonder dit filter noemt het venster afnemers van een land
   * dat er niet meer in zit — de keuze blijft namelijk staan als de klant de doelmarktkolom
   * verandert, en dan zou het venster iets beloven wat niet gebeurt.
   */
  targetMarkets?: string[];
  isSubmitting: boolean;
  onConfirm: () => void;
}

/** EXPERIENCE.md KF-1: submit always ends in an explicit confirmation dialog with a summary. */
export function ConfirmSubmitDialog({ open, onOpenChange, columnCount, changedCount, totalProductCount, productChangeCount, retailerChoice = [], targetMarkets = [], isSubmitting, onConfirm }: ConfirmSubmitDialogProps) {
  const { t } = useTranslation();

  function afnemerregel(entry: RetailerChoiceEntry): string {
    const namen = entry.retailers.map((retailer) => retailer.name);
    const getoond = namen.length > MAX_NAMEN ? namen.slice(0, GETOOND_BIJ_AFKAPPEN) : namen;
    const rest = namen.length - getoond.length;
    const staart = rest > 0 ? [t('intake.review.confirmDialog.retailerMore', { count: rest })] : [];
    return t('intake.review.confirmDialog.retailerLine', {
      market: marketName(t, entry.targetMarket),
      names: [...getoond, ...staart].join(', '),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="intake-confirm-submit-dialog">
        <DialogHeader>
          <DialogTitle>{t('intake.review.confirmDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('intake.review.confirmDialog.summary', { columnCount, changedCount })}
          </DialogDescription>
          {totalProductCount > 0 ? (
            <DialogDescription data-testid="intake-confirm-dialog-product-summary">
              {t('intake.review.confirmDialog.productSummary', { totalProductCount, productChangeCount })}
            </DialogDescription>
          ) : null}
          {/* Gewone alinea's en geen `DialogDescription`: die meldt zichzelf aan als de énige
              omschrijving van het venster, dus een tweede regel zou de eerste overschrijven en een
              schermlezer zou alleen de laatste voorlezen. */}
          {retailerChoice
            .filter((entry) => targetMarkets.includes(entry.targetMarket))
            .map((entry) => (
              <p key={entry.targetMarket} data-testid={`intake-confirm-dialog-retailers-${entry.targetMarket}`} className="text-sm text-[var(--foreground-muted)]">
                {afnemerregel(entry)}
              </p>
            ))}
        </DialogHeader>
        <DialogFooter>
          <DialogClose>
            <Button variant="outline" data-testid="intake-confirm-dialog-cancel">{t('common.cancel')}</Button>
          </DialogClose>
          <Button data-testid="intake-confirm-dialog-submit" disabled={isSubmitting} onClick={onConfirm}>
            {t('intake.review.confirmDialog.confirmCta')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
